import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    forLifeOf,
    getDynamicRouteLink,
    getRouterLinkUrlById,
    ISelectOption,
    getRouterLinkById,
    LifecycleAware
} from '@lsq-frontend/common-ui';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
    ADMIN_MANAGE_SUPPLIER,
    ADMIN_MANAGE_SUPPLIER_IMPLEMENTATION,
    ADMIN_MANAGE_SUPPLIER_INFORMATION,
    ADMIN_MANAGE_SUPPLIER_PAYMENT_METHODS,
    ADMIN_SUPPLIERS,
    atlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { AdminSuppliersService } from '../admin-suppliers.service';
import { ManageSupplierService } from './manage-supplier.service';

@LifecycleAware()
@Component({
    selector: 'manage-supplier',
    styleUrls: ['./manage-supplier.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <div class="manage-supplier">
            <page-header
                [titleText]="(currentSupplierRelationship$ | async)?.supplierPreferredName"
                [shortBottomPadding]="true"
                backButtonText="Back To Suppliers"
                (handleBackButtonClick)="navigateToSuppliersPage()"
            >
                <div class="buyer-select">
                    <div class="buyer-select-label">Buyer Relationship</div>
                    <lsq-select
                        *ngIf="relationshipOptions$ | async"
                        [disabled]="(relationshipOptions$ | async).length === 1"
                        [options]="relationshipOptions$ | async"
                        parentClass="lg"
                        [ngModel]="(currentSupplierRelationship$ | async).relationshipKey"
                        (ngModelChange)="changeCurrentRelationship($event)"
                    ></lsq-select>
                </div>
            </page-header>
            <div class="container-fluid page-container">
                <tabs [tabsConfig]="tabsConfig$ | async"></tabs>
                <router-outlet></router-outlet>
            </div>
        </div>
    `
})
export class ManageSupplierComponent {
    public currentSupplierRelationship$ = this._manageSupplierService.currentSupplierRelationship$;
    public relationshipOptions$: Observable<
        ISelectOption[]
    > = this._manageSupplierService.currentSupplierRelationship$.pipe(
        map((relationship) => {
            if (relationship) {
                return relationship.allSellerRelationships.map((it) => ({
                    label: it?.buyerPreferredName || '',
                    value: it?.relationshipKey || ''
                }));
            }
            return null;
        })
    );

    public tabsConfig$ = this.currentSupplierRelationship$.pipe(
        map((relationship) => {
            if (relationship) {
                return [
                    {
                        label: 'Supplier Information',
                        routerLink: getDynamicRouteLink(
                            getRouterLinkUrlById(atlas, ADMIN_MANAGE_SUPPLIER_INFORMATION),
                            {
                                relationshipKey: relationship.relationshipKey
                            }
                        )
                    },
                    {
                        label: 'Implementation',
                        routerLink: getDynamicRouteLink(
                            getRouterLinkUrlById(atlas, ADMIN_MANAGE_SUPPLIER_IMPLEMENTATION),
                            {
                                relationshipKey: relationship.relationshipKey
                            }
                        )
                    },
                    {
                        label: 'Payment Methods',
                        routerLink: getDynamicRouteLink(
                            getRouterLinkUrlById(atlas, ADMIN_MANAGE_SUPPLIER_PAYMENT_METHODS),
                            {
                                relationshipKey: relationship.relationshipKey
                            }
                        )
                    }
                ];
            }
            return null;
        })
    );

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _manageSupplierService: ManageSupplierService,
        private _adminSupplierService: AdminSuppliersService,
        private _router: Router
    ) {
        this._activatedRoute.params.pipe(forLifeOf(this)).subscribe((params) => {
            this._manageSupplierService.loadRelationship(params?.relationshipKey);
        });

        this._manageSupplierService.currentSupplierRelationship$
            .pipe(
                forLifeOf(this),
                filter((relationship) => !!relationship)
            )
            .subscribe(async (relationship) => {
                if (relationship) {
                    await this._manageSupplierService.loadSupplierDetails(relationship.supplierKey);
                }
            });
    }

    public changeCurrentRelationship(relationshipKey: string): void {
        this._router.navigateByUrl(
            getDynamicRouteLink(getRouterLinkUrlById(atlas, ADMIN_MANAGE_SUPPLIER), {
                relationshipKey
            })
        );
    }

    public navigateToSuppliersPage(): void {
        // Refetch the data for suppliers list.
        if (this._adminSupplierService.dataSource) {
            this._adminSupplierService.dataSource.dispatchRequest();
        }
        this._router.navigate(getRouterLinkById(atlas, ADMIN_SUPPLIERS), {
            queryParamsHandling: 'preserve'
        });
    }
}
